/**
 * Comments plan 3: change requests in a comments list (editor panel and preview panel). The canvas applies and checks
 * them (KEditor relays); here the outcome becomes the thread's resolution. Needs rows, languageId, translationSetup,
 * load() on the component.
 */
import InsertPageCommentResolution from '@/graphQlQueries/mutations/insertPageCommentResolution';
import { toThreads, suggestionItems, languageHandleFor } from '@/utils/comments';

const WAIT_MS = 16000;

export default {
  data: () => ({
    staleIds: [],
    // Requests being applied or rejected right now: a second click waits (a double apply could change the text twice).
    pendingIds: [],
  }),
  methods: {
    handleOf(languageId) {
      return languageHandleFor(this.translationSetup, languageId);
    },
    /** Asks the canvas through KEditor; null when nobody answers in time. */
    askCanvas(event, payload) {
      return new Promise((resolve) => {
        let settled = false;
        const timer = setTimeout(() => { if (!settled) { settled = true; resolve(null); } }, WAIT_MS);
        this.$root.$emit(event, payload, (answer) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          resolve(answer);
        });
      });
    },
    async checkSuggestions() {
      const items = suggestionItems(toThreads(this.rows, this.languageId), this.handleOf);
      if (!items.length) {
        this.staleIds = [];
        return;
      }
      const answer = await this.askCanvas('keditor-check-suggestions', items);
      this.staleIds = (answer && answer.stale) || [];
    },
    async settle(thread, outcome) {
      await this.$apollo.mutate({ mutation: InsertPageCommentResolution, variables: { commentId: thread.id, outcome } });
      await this.load();
      this.$root.$emit('keditor-comments-changed');
    },
    claim(id) {
      if (this.pendingIds.includes(id)) return false;
      this.pendingIds = [...this.pendingIds, id];
      return true;
    },
    release(id) {
      this.pendingIds = this.pendingIds.filter((x) => x !== id);
    },
    async applySuggestion(thread) {
      if (!this.claim(thread.id)) return;
      try {
        await this.applyOnce(thread);
      } finally {
        this.release(thread.id);
      }
    },
    async applyOnce(thread) {
      const [item] = suggestionItems([thread], this.handleOf);
      if (!item) return;
      const answer = await this.askCanvas('keditor-apply-suggestion', { ...item, text: thread.comment.suggestion.new });
      if (!answer) {
        // Nobody answered: the editor is busy or gone; the text may be just fine.
        this.$bvToast.toast(this.$t('webmag.comments.noCanvas'), { variant: 'warning', solid: true });
        return;
      }
      try {
        if (answer.ok) {
          await this.settle(thread, 'applied');
          this.$emit('applied');
          return;
        }
      } catch (e) {
        this.$bvToast.toast(this.$t('webmag.comments.appliedNotMarked'), { variant: 'danger', solid: true });
        return;
      }
      if (answer.reason === 'language') {
        const lang = (item.language || (this.translationSetup && this.translationSetup.main) || '').toUpperCase();
        this.$bvToast.toast(this.$t('webmag.comments.switchLanguage', { lang }), { variant: 'warning', solid: true });
        return;
      }
      this.$bvToast.toast(this.$t('webmag.comments.staleToast'), { variant: 'warning', solid: true });
      if (!this.staleIds.includes(thread.id)) this.staleIds = [...this.staleIds, thread.id];
    },
    async rejectSuggestion(thread) {
      if (!this.claim(thread.id)) return;
      try {
        await this.settle(thread, 'rejected');
      } catch (e) {
        this.$bvToast.toast(this.$t('webmag.comments.notSaved'), { variant: 'danger', solid: true });
      } finally {
        this.release(thread.id);
      }
    },
  },
};
